import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useNetworkState } from 'react-use';
import { notificationManager } from '../utils/notificationManager';

export const Context = createContext({});

export const Provider = ({ children }) => {
  const [is2G, setIs2G] = useState(false);
  const network = useNetworkState();
  const [networkState, setNetworkState] = useState({
    online: network.online,
  });
  const isMountedRef = useRef(true);
  const slowConnectionWarningShown = useRef(false);
  // Tracks whether the user intentionally went offline via the manual toggle.
  // When true, auto-recovery from a real connection drop won't override their choice.
  const userManuallyOfflineRef = useRef(false);

  const manualReconnect = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
    userManuallyOfflineRef.current = false;
    setNetworkState({ online: true });
  }, []);

  const manualGoOffline = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
    userManuallyOfflineRef.current = true;
    setNetworkState({ online: false });
  }, []);

  const toggleOnlineMode = useCallback(() => {
    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
    setNetworkState((prev) => ({ online: !prev.online }));
  }, []);

  useEffect(() => {
    if (network.online && isMountedRef.current) {
      setIs2G(network.effectiveType === '2g');
    } else {
      setIs2G(false);
    }
  }, [network.effectiveType, network.online]);

  // Bidirectional auto-sync with the real network state.
  // Going truly offline always updates networkState regardless of manual mode.
  // Coming back online only auto-restores if the user didn't intentionally go offline.
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (!network.online) {
      setNetworkState({ online: false });
    } else if (!userManuallyOfflineRef.current) {
      setNetworkState({ online: true });
    }
  }, [network.online]);

  useEffect(() => {
    // Set up notification manager with online state callback
    notificationManager.setOnlineStateCallback(() => networkState.online);

    return () => {
      // Clean up notification manager callback
      notificationManager.setOnlineStateCallback(null);
    };
  }, [networkState.online]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Hide notifications when going offline
  useEffect(() => {
    if (!networkState.online) {
      notificationManager.hideAllNotifications();
    }
  }, [networkState.online]);

  // Show warning when connection is slow and user is online
  useEffect(() => {
    if (is2G && networkState.online && !slowConnectionWarningShown.current) {
      const timer = setTimeout(() => {
        notificationManager.showWarning(
          'Internet konekcija je spora (2G). Preporučujemo prebacivanje u offline režim za brži rad.',
          6,
        );
        slowConnectionWarningShown.current = true;
      }, 2000);

      return () => {
        clearTimeout(timer);
      };
    }

    // Reset warning flag when user goes offline
    if (!networkState.online) {
      slowConnectionWarningShown.current = false;
    }
  }, [is2G, networkState.online]);

  const offlineContext = useMemo(
    () => ({
      is2G,
      networkState,
      setNetworkState,
      isLocallyOffline: !networkState.online && network.online,
      manualReconnect,
      manualGoOffline,
      toggleOnlineMode,
    }),
    [is2G, networkState, network.online, manualReconnect, manualGoOffline, toggleOnlineMode],
  );

  return <Context.Provider value={offlineContext}>{children}</Context.Provider>;
};

export const { Consumer } = Context;
export const useOfflineModeContext = () => useContext(Context);
